import { type ApiContainerType, type ApiService, EnumContainerActionRequest } from '@Model';
import {
    Button,
    FieldWithError,
    FlexColumn,
    FloatingCard,
    type ILinkInputProperties,
    InlineBanner,
    LabeledDiv,
    NumberStepper,
    TextInput,
    useTranslate,
    useWizardErrors,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { useState } from 'react';
import {
    type ContainerPosition,
    type ContainerStockLine,
    lineOf,
    maxCountFor,
    MIN_COUNT,
    needsReview,
} from '../../containerOrder';
import classes from '../ContainerSteps.module.css';
import { ContainerPositionForm } from './ContainerPositionForm';
import { StockLineList } from './StockLineList';
import { ContentField } from './ContentField';
import { blockingDetailsError, contentError, type DetailsStepErrorKey } from './detailsErrors';

/** The stock as the step sees it, whether it arrived or not. */
export interface ContainerStockView {
    lines: ContainerStockLine[];
    isLoading: boolean;
    /** Why the stock could not be read, empty while it could. */
    failure: string;
    reload: () => void;
}

/** What happens to the picked container, which is all the step words differently. */
export type StockAction =
    | EnumContainerActionRequest.Collect
    | EnumContainerActionRequest.Empty
    | EnumContainerActionRequest.Exchange;

interface StockStepBodyProps {
    action: StockAction;
    stock: ContainerStockView;
    /** The container this order asks for, undefined while nothing is selected or described. */
    position: ContainerPosition | undefined;
    containerTypeField: ILinkInputProperties<ApiContainerType>;
    contentField: ILinkInputProperties<ApiService>;
    /** The waste code of the chosen content, empty while none is known. */
    avvCode: string;
    onTakeLine: (line: ContainerStockLine) => void;
    onCount: (count: number) => void;
    onContainerNumber: (containerNumber: string) => void;
    onLocationHint: (locationHint: string) => void;
    onRemove: () => void;
}

const DESCRIBE_HINT = 'Diese Position hat keinen Bestandsbezug — sie wird vor der Tour geprüft.';

/** The question the step asks and the promise below it, per action. */
const WORDING: Record<StockAction, { title: string; subtitle: string }> = {
    [EnumContainerActionRequest.Collect]: {
        title: 'Welche Container sollen abgeholt werden?',
        subtitle: 'Wähle einen Container — eine Abholung, eine Tour.',
    },
    [EnumContainerActionRequest.Empty]: {
        title: 'Welcher Container soll geleert werden?',
        subtitle: 'Wähle einen Container — eine Leerung, eine Tour.',
    },
    [EnumContainerActionRequest.Exchange]: {
        title: 'Welcher Container soll getauscht werden?',
        subtitle: 'Wähle einen Container — den Ersatz wählst du danach.',
    },
};

/**
 * Step 3 of a pickup or an emptying: the containers we have recorded at the site, as a selection list.
 *
 * An order is about one container, so the list is single-select and what goes in it is asked once,
 * below the list, rather than on every line. Every pick is saved as it happens. The stock never
 * blocks the order — a container the customer knows of but our records do not can always be
 * described, and a count above what we recorded is a hint rather than a limit.
 */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
export function StockStepBody({
    action,
    stock,
    position,
    containerTypeField,
    contentField,
    avvCode,
    onTakeLine,
    onCount,
    onContainerNumber,
    onLocationHint,
    onRemove,
}: StockStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<DetailsStepErrorKey>();
    const [isDescribing, setIsDescribing] = useState(false);
    const [pickedLineId, setPickedLineId] = useState<string | undefined>(undefined);

    const describedPosition = position?.origin === 'described' ? position : undefined;
    const stockPosition = position?.origin === 'stock' ? position : undefined;
    const selectedLineId = stockPosition ? lineOf(stock.lines, stockPosition, pickedLineId)?.id : undefined;

    // Without a recorded container there is nothing to select, so the description form takes over
    // instead of leaving an empty screen behind; discarding what it holds brings the list back.
    const hasNoStock = !stock.isLoading && !stock.failure && stock.lines.length === 0;
    const isForced = hasNoStock || !!stock.failure;
    const showForm = isDescribing || describedPosition !== undefined || isForced;

    const discardDescription = () => {
        onRemove();
        setIsDescribing(false);
    };

    const toggleLine = (line: ContainerStockLine) => {
        if (selectedLineId === line.id) {
            onRemove();
            return;
        }
        setPickedLineId(line.id);
        onTakeLine(line);
    };

    return (
        <>
            <WizardStepTitle
                title={i18n(WORDING[action].title)}
                subtitle={
                    hasNoStock
                        ? i18n('Beschreib den Container kurz — wir gleichen das vor der Tour ab.')
                        : i18n(WORDING[action].subtitle)
                }
            />

            <FieldWithError error={blockingDetailsError(errors, position)}>
                <FlexColumn align="stretch" gap="md">
                    {hasNoStock && (
                        <InlineBanner
                            variant="info"
                            title={i18n(
                                'An diesem Leistungsort steht laut unseren Aufzeichnungen kein Container von uns.',
                            )}
                        />
                    )}

                    {stock.failure && (
                        <InlineBanner
                            variant="warning"
                            title={i18n('Wir konnten deinen Bestand gerade nicht laden.')}
                            action={
                                <Button
                                    appearance="transparent"
                                    size="xs"
                                    onClick={stock.reload}
                                    label={i18n('Erneut versuchen')}
                                >
                                    {i18n('Erneut versuchen')}
                                </Button>
                            }
                        />
                    )}

                    {!hasNoStock && !stock.failure && (
                        <StockLineList
                            stock={stock}
                            selectedLineId={selectedLineId}
                            selectedNeedsReview={!!stockPosition && needsReview(stockPosition)}
                            dimmed={showForm}
                            onSelect={toggleLine}
                            footer={
                                !showForm && (
                                    <Button
                                        appearance="transparent"
                                        size="sm"
                                        className={classes.cardFooterLink}
                                        onClick={() => setIsDescribing(true)}
                                    >
                                        {i18n('Container nicht dabei?')}
                                    </Button>
                                )
                            }
                        />
                    )}

                    {/* Eine Bestellung, ein Container — der Inhalt wird einmal unter der Liste
                        gefragt statt an jeder Zeile. */}
                    {stockPosition && (
                        <FloatingCard title={i18n('Was ist drin?')} separateHeader={false} collapsible={false}>
                            <FlexColumn align="stretch" gap="sm">
                                {stockPosition.usesIndividualManagement === false && (
                                    <LabeledDiv label={i18n('Anzahl')} className={classes.countField}>
                                        <NumberStepper
                                            name="container_count"
                                            ariaLabel={stockPosition.containerTypeLabel}
                                            value={stockPosition.count}
                                            min={MIN_COUNT}
                                            max={maxCountFor(stockPosition)}
                                            editable
                                            warning={needsReview(stockPosition)}
                                            onChange={(count) => onCount(count)}
                                        />
                                    </LabeledDiv>
                                )}

                                <ContentField
                                    contentField={contentField}
                                    avvCode={avvCode}
                                    error={contentError(errors, stockPosition)}
                                />

                                <TextInput
                                    name="location_hint"
                                    label={`${i18n('Wo steht er?')} (${i18n('optional')})`}
                                    placeholder={i18n('z. B. hinten an Tor 3')}
                                    value={stockPosition.locationHint}
                                    onChange={() => true}
                                    onConfirm={(value) => {
                                        onLocationHint(value);
                                        return true;
                                    }}
                                />

                                {needsReview(stockPosition) && (
                                    <InlineBanner
                                        variant="warning"
                                        title={`${i18n('Bei uns sind hier nur')} ${stockPosition.stockCount ?? 0} ${i18n(
                                            'erfasst - geht klar, wir prüfen das vor der Tour.',
                                        )}`}
                                    />
                                )}
                            </FlexColumn>
                        </FloatingCard>
                    )}

                    {showForm && (
                        <ContainerPositionForm
                            origin="described"
                            title={hasNoStock ? i18n('Welcher Container steht da?') : i18n('Container nicht dabei?')}
                            subtitle={
                                hasNoStock
                                    ? i18n('Typ und Inhalt reichen — Nummer und Standort helfen dem Fahrer.')
                                    : i18n('Beschreibe ihn kurz — wir gleichen das vor der Tour ab.')
                            }
                            hint={i18n(DESCRIBE_HINT)}
                            onContainerNumber={onContainerNumber}
                            location={{ label: i18n('Wo steht er?'), onChange: onLocationHint }}
                            position={describedPosition}
                            containerTypeField={containerTypeField}
                            contentField={contentField}
                            avvCode={avvCode}
                            contentError={contentError(errors, describedPosition)}
                            onCount={onCount}
                            // Ohne Bestand ist das Formular der Schritt selbst und hat nichts, wohin
                            // es sich schließen könnte.
                            onDiscard={isForced ? undefined : discardDescription}
                        />
                    )}
                </FlexColumn>
            </FieldWithError>
        </>
    );
}
